import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { BiEdit } from "react-icons/bi";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useState, useEffect, useRef } from "react";
import CustomPagination from "../TablePagination/TablePagination";
import { formatDateTime } from "../../utils/dateTime";
import { useNavigate } from "react-router-dom";
import { FiEye } from "react-icons/fi";
import { getExpenseTypeName } from "../../utils/ExpenseConverter";
import {
  getLoanTypeLabel,
  getLoanStatusLabel,
  getLoanBalance,
} from "../../utils/LoanConverter";
import { MdOutlinePassword, MdHistory } from "react-icons/md";
import { TbAdjustmentsHorizontal } from "react-icons/tb";
import RowActionsMenu from "../RowActionsMenu/RowActionsMenu";
import BranchChangePasswordModal from "../Modals/BranchChangePasswordModal";
import { DeleteInvoiceItemAPI } from "../../ApiRequests";
import { fetchCustomerInvoice } from "../../store/Slices/CustomerInvoiceSlice";
import { fetchCustomerReturnInvoice } from "../../store/Slices/CustomerReturnInvoiceSlice";
import { SuccessToast } from "../../utils/ShowToast";
import { useDispatch } from "react-redux";
import { getInvoicePartyName } from "../../utils/invoice";
import { getAppBasePath } from "../../utils/appPaths";

export default function TableComp({
  setOpenEditModal,
  setOpenDeleteModal,
  setSelected,
  setOpenQtyAdjustModal,
  setOpenQtyLogModal,
  enableAccountLog,
  enableLoanDetail,
  onAddDiscount,
  onViewDiscountLog,
  onViewLedger,
  onOpeningBalance,
  onViewPayments,
  onAddPayment,
  onAddNonProductInvoice,
  AllRows,
  setRows,
  Columns,
  CurrentInvoice,
  SelectedCustomer,
  isLedger,
  onInvoiceUpdated,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const modalRef = useRef(null);
  const navigate = useNavigate();
  const [OpenChangepasswordModal, setOpenChangepasswordModal] = useState(false);
  const [SelectedData, setSelectedData] = useState({});
  const dispatch = useDispatch();
  const [Loading, setLoading] = useState(false);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setOpenEditModal(false);
        setOpenDeleteModal(false);
        setSelected(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  const commonCellStyle = {
    fontWeight: 400,
    fontFamily: "Quicksand",
    border: "1px solid #E5E9EB",
    color: "#1A2426",
  };

  return (
    <div>
      <TableContainer component={Paper} sx={{ boxShadow: "none", borderRadius: 0 }}>
        <Table aria-label="customized table">
          <TableHead>
            <TableRow
              sx={{
                borderTop: "1px solid #155f60",
                backgroundColor: "#155f60",
              }}
            >
              {Columns.map((dt, i) => (
                <TableCell
                  key={i}
                  sx={{
                    fontWeight: "bold",
                    fontFamily: "Quicksand",
                    border: "1px solid #155f60",
                    backgroundColor: "#155f60",
                    ...(dt.id === "name" && dt.type === "Ledger"
                      ? { flex: 1 }
                      : {}),
                  }}
                  align="center"
                >
                  <div className="text-sm py-2 whitespace-nowrap font-quicksand text-white font-bold tracking-wide">
                    {dt.title}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {AllRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={Columns.length || 1}
                  align="center"
                  sx={{ border: "1px solid #E5E9EB", color: "#8A9496" }}
                >
                  <div className="py-10 text-sm text-muted">
                    No records to display.
                  </div>
                </TableCell>
              </TableRow>
            )}
            {AllRows.slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage
            ).map((Data, rowIndex) => (
              <TableRow
              hover
              key={Data._id ?? Data.draftId ?? rowIndex}
              sx={
                rowIndex === AllRows.length - 1 && isLedger
                  ? {
                      backgroundColor: "#155f60",
                      color: "#ffffff",
                      "&:hover": { backgroundColor: "#155f60" },
                    }
                  : {}
              }
            >
                {Columns.map((column, i) => {
                  let currentType;
                  if (column.id === "type") {
                    currentType = getExpenseTypeName(Data);
                  }

                  const getFormattedValue = () => {
                    if (column.id === "category_id")
                      return Data?.category_id?.name;
                    if (column.id === "companyId") return Data?.companyId?.name;
                    if (column.id === "customerId")
                      return getInvoicePartyName(Data) || Data?.customerId?.name;
                    if (column.id === "categoryId")
                      return Data?.categoryId?.name;
                    if (column.id === "subcategoryId")
                      return Data?.subcategoryId?.name;
                    if (column.id === "supplierId")
                      return Data?.supplierId?.name;
                    if (column.id === "itemId") return Data?.itemId?.name;
                    if (column.id === "payment_type")
                      return Data.payment_type === 1 ? "Cash" : "Bank";
                    if (column.id === "accountId")
                      return (
                        Data?.accountId?.account_name ||
                        Data?.account_name ||
                        "—"
                      );
                    if (column.id === "type") return currentType;
                    if (column.id === "loanType") return getLoanTypeLabel(Data);
                    if (column.id === "amount" && Data.totalDisbursed != null) {
                      return new Intl.NumberFormat("en-PK", {
                        style: "currency",
                        currency: "PKR",
                      }).format(Number(Data.totalDisbursed || Data.amount || 0));
                    }
                    if (column.id === "paidAmount" && Data.totalRepaid != null) {
                      return new Intl.NumberFormat("en-PK", {
                        style: "currency",
                        currency: "PKR",
                      }).format(Number(Data.totalRepaid || Data.paidAmount || 0));
                    }
                    if (column.id === "status")
                      return getLoanStatusLabel(Data);
                    if (column.id === "balance")
                      return new Intl.NumberFormat("en-PK", {
                        style: "currency",
                        currency: "PKR",
                      }).format(getLoanBalance(Data));
                    if (
                      [
                        "total",
                        "amount",
                        "payable",
                        "total_amount",
                        "paid",
                        "paidAmount",
                        "purchase",
                        "sale",
                        "sale_shop",
                        "expense",
                        "remaining",
                        "discount",
                        "recieveable",
                        "recieved",
                        "opening_balance",
                        "return_amount",
                      ].includes(column.id)
                    ) {
                      return new Intl.NumberFormat("en-PK", {
                        style: "currency",
                        currency: "PKR",
                      }).format(Number(Data[column.id]) || 0);
                    }
                    if (column.id === "date" && column.type === "stock-stats") {
                      return Data.date;
                    }
                    if (column.id === "date") {
                      return formatDateTime(Data[column.id]);
                    }
                    if (column.id === "catId") return Data[column.id]?.name;
                    return Data[column.id];
                  };

                  if (column.id === "payments") {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-2 font-poppins">
                          <FiEye
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                            onClick={() => {
                              if (column.type === "Customer")
                                navigate(
                                  "/branch/customers-payments/" + Data._id
                                );
                              else
                                navigate(
                                  "/branch/company-payments/" + Data._id
                                );
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  }

                  if (column.id === "amount" && column.type === "ItemStats") {
                    console.log(Data);

                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div
                          className={`font-quicksand maxWeb1:text-[1.5rem] maxWeb2:text-[1.8rem] maxWeb3:text-[2rem] maxWeb4:text-[2.2rem] text-[1rem] text-center font-poppins ${
                            rowIndex === AllRows.length - 1 &&
                            isLedger &&
                            "text-white"
                          } ${
                            column.id === "dr" && Data["dr"] > 0 && "text-[red]"
                          } ${
                            (column.id === "name" ||
                              column.id === "companyId") &&
                            "whitespace-nowrap"
                          }`}
                        >
                          {Data?.amount || 123}{" "}
                        </div>
                      </TableCell>
                    );
                  }

                  if (column.id === "change_password") {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-2 font-poppins">
                          <MdOutlinePassword
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                            onClick={() => {
                              setOpenChangepasswordModal(true);
                              setSelectedData(Data);
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  } else if (column.id === "customer_payment_actions") {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-2 font-poppins">
                          {setOpenEditModal && (
                            <BiEdit
                              className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                              title="Edit payment"
                              onClick={() => {
                                setSelected(Data);
                                setOpenEditModal(true);
                              }}
                            />
                          )}
                          <RiDeleteBin5Line
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-danger transition-colors duration-200"
                            onClick={() => {
                              setSelected(Data);
                              setOpenDeleteModal(true);
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  } else if (
                    column.id === "actions" &&
                    column.type === "entity-menu"
                  ) {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <RowActionsMenu
                          row={Data}
                          onEdit={(r) => {
                            setSelected(r);
                            setOpenEditModal(true);
                          }}
                          onDelete={(r) => {
                            setSelected(r);
                            setOpenDeleteModal(true);
                          }}
                          onAddDiscount={onAddDiscount}
                          onViewDiscountLog={onViewDiscountLog}
                          onViewLedger={onViewLedger}
                          onOpeningBalance={onOpeningBalance}
                          onViewPayments={onViewPayments}
                          onAddPayment={onAddPayment}
                          onAddNonProductInvoice={onAddNonProductInvoice}
                        />
                      </TableCell>
                    );
                  } else if (column.id === "actions" && column.type === "product") {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-1 flex-wrap font-poppins">
                          <BiEdit
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                            onClick={() => {
                              setSelected(Data);
                              setOpenEditModal(true);
                            }}
                          />
                          {setOpenQtyAdjustModal && (
                            <TbAdjustmentsHorizontal
                              title="Adjust quantity"
                              className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                              onClick={() => {
                                setSelected(Data);
                                setOpenQtyAdjustModal(true);
                              }}
                            />
                          )}
                          {setOpenQtyLogModal && (
                            <MdHistory
                              title="Adjustment log"
                              className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                              onClick={() => {
                                setSelected(Data);
                                setOpenQtyLogModal(true);
                              }}
                            />
                          )}
                          <RiDeleteBin5Line
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-danger transition-colors duration-200"
                            onClick={() => {
                              setSelected(Data);
                              setOpenDeleteModal(true);
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  } else if (column.id === "actions") {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-2 font-poppins">
                          {enableAccountLog && Data?._id && (
                            <MdHistory
                              title="Account log"
                              className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                              onClick={() => {
                                navigate(
                                  `${getAppBasePath()}/accounts/${Data._id}/log`
                                );
                              }}
                            />
                          )}
                          {enableLoanDetail && Data?._id && (
                            <FiEye
                              title="View loan log"
                              className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                              onClick={() => {
                                navigate(
                                  `${getAppBasePath()}/loans/${Data._id}`
                                );
                              }}
                            />
                          )}
                          {setOpenEditModal && (
                            <BiEdit
                              className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                              onClick={() => {
                                if (column.type === "mcqs") {
                                  navigate("/admin/mcqs/edit/" + Data._id);
                                } else {
                                  setSelected(Data);
                                  setOpenEditModal(true);
                                }
                              }}
                            />
                          )}
                          {setOpenDeleteModal && (
                            <RiDeleteBin5Line
                              className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-danger transition-colors duration-200"
                              onClick={() => {
                                setSelected(Data);
                                setOpenDeleteModal(true);
                              }}
                            />
                          )}
                        </div>
                      </TableCell>
                    );
                  } else if (column.id === "company_invoice_actions") {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-2 font-poppins">
                          <BiEdit
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                            onClick={() => {
                              setSelected(Data);
                              setOpenEditModal(true);
                            }}
                          />
                          <RiDeleteBin5Line
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-danger transition-colors duration-200"
                            onClick={() => {
                              setSelected(Data);
                              setOpenDeleteModal(true);
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  } else if (
                    column.id === "invoice_actions" &&
                    column.type === "edit"
                  ) {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-2 font-poppins">
                          {!Loading && (
                            <RiDeleteBin5Line
                              className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                              onClick={async () => {
                                setLoading(true);
                                try {
                                  const response = await DeleteInvoiceItemAPI(
                                    Data._id
                                  );
                                  if (response.data.success) {
                                    SuccessToast(
                                      "Invoice item deleted successfully"
                                    );
                                    dispatch(
                                      fetchCustomerInvoice(SelectedCustomer._id)
                                    );
                                    dispatch(
                                      fetchCustomerReturnInvoice(
                                        SelectedCustomer._id
                                      )
                                    );
                                    onInvoiceUpdated?.();
                                  }
                                } catch (error) {
                                  console.log(error);
                                }
                                setLoading(false);
                              }}
                            />
                          )}
                          <BiEdit
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-danger transition-colors duration-200"
                            onClick={() => {
                              setSelected(Data);
                              setOpenEditModal(true);
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  } else if (column.id === "actions_stock_draft") {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-2 font-poppins">
                          <BiEdit
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-main transition-colors duration-200"
                            onClick={() => {
                              setSelected?.(Data);
                              setOpenEditModal?.(true);
                            }}
                          />
                          <RiDeleteBin5Line
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-danger transition-colors duration-200"
                            onClick={() => {
                              if (!setRows || !Data?.draftId) return;
                              setRows((prev) =>
                                (Array.isArray(prev) ? prev : AllRows).filter(
                                  (row) => row.draftId !== Data.draftId
                                )
                              );
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  } else if (column.id === "actions_invoice_delete") {
                    return (
                      <TableCell key={i} sx={commonCellStyle} align="center">
                        <div className="flex justify-center items-center gap-x-2 font-poppins">
                          <RiDeleteBin5Line
                            className="text-[1.2rem] maxWeb1:text-[2rem] maxWeb2:text-[2.5rem] maxWeb3:text-[3rem] maxWeb4:text-[3rem] cursor-pointer hover:text-danger transition-colors duration-200"
                            onClick={() => {
                              const filteredData = AllRows.filter(
                                (dt, i) => rowIndex !== i
                              );
                              setRows(filteredData);
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell key={i} sx={commonCellStyle} align="center">
                      <div
                        className={`font-quicksand maxWeb1:text-[1.5rem] maxWeb2:text-[1.8rem] maxWeb3:text-[2rem] maxWeb4:text-[2.2rem] text-[1rem] text-center font-poppins ${
                          rowIndex === AllRows.length - 1 &&
                          isLedger &&
                          "text-white"
                        } ${
                          column.id === "dr" && Data["dr"] > 0 && "text-[red]"
                        } ${
                          (column.id === "name" || column.id === "companyId") &&
                          "whitespace-nowrap"
                        }`}
                      >
                        {getFormattedValue()}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {OpenChangepasswordModal && (
          <BranchChangePasswordModal
            Open={OpenChangepasswordModal}
            setOpen={setOpenChangepasswordModal}
            CurrentBranch={SelectedData}
          />
        )}
      </TableContainer>
      <CustomPagination
        count={AllRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        RowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}
